import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { surveyAPI } from '../../services/api';
import {
  SURVEY_ID,
  SURVEY_TITLE,
  INTRO_TEXT,
  SECTIONS,
  questionNumber,
  isVisible,
  countAnswered,
  unansweredNumbers,
  questionNumbers,
  rangeLabel,
  pruneAnswers,
} from './surveyQuestions';
import ResponseSummary from './ResponseSummary';

// ---------------------------------------------------------------------------
// Clinician "Pre-Exposure Survey".
//
// One screen per page of ./surveyQuestions.js (a few questions each), grouped
// into sections shown in the sidebar. Every question is optional. Answers are
// autosaved as the user goes (PUT /api/survey/response) so they can leave and
// resume. After submitting, the user sees a copy of their full response and can
// edit it: edits autosave as a draft and are resubmitted (or discarded) as a
// whole; the server keeps every submitted version.
// ---------------------------------------------------------------------------

// Steps: 0 = intro, 1..N = survey pages, N + 1 = review.
const PAGES = SECTIONS.flatMap((section, sectionIndex) =>
  section.pages.map((page, pageIndex) => ({ section, sectionIndex, page, pageIndex }))
);
const INTRO_STEP = 0;
const REVIEW_STEP = PAGES.length + 1;
// Submitted at least once (completed_at marks the first submission).
const wasSubmitted = (sub) => sub.count > 0 || !!sub.firstAt;
const firstStepOf = (sectionIndex) => PAGES.findIndex((p) => p.sectionIndex === sectionIndex) + 1;
// Question number -> the step (page) it is on, for "jump to Q5" links.
const STEP_OF_QUESTION = {};
PAGES.forEach((p, i) => questionNumbers([p.page]).forEach((n) => { STEP_OF_QUESTION[n] = i + 1; }));

// Tailwind needs literal class names, so map counts to classes.
const SM_COLS = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-5' };

// Height reveal for content that appears conditionally. Overflow is only
// hidden while animating so focus rings and shadows aren't clipped after.
const REVEAL = {
  initial: { opacity: 0, height: 0, overflow: 'hidden' },
  animate: { opacity: 1, height: 'auto', transitionEnd: { overflow: 'visible' } },
  exit: { opacity: 0, height: 0, overflow: 'hidden' },
  transition: { duration: 0.2 },
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return '';
  }
};

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

const ChoiceList = ({ q, answers, setAnswer }) => {
  const multi = q.type === 'multi';
  const value = answers[q.id];
  const selected = (v) => (multi ? Array.isArray(value) && value.includes(v) : value === v);
  const otherChosen = q.options.some((o) => o.other && selected(o.value));

  const toggle = (v) => {
    if (multi) {
      const cur = Array.isArray(value) ? value : [];
      setAnswer(q.id, cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
    } else {
      // Clicking the chosen option again clears it (every question is optional).
      setAnswer(q.id, value === v ? null : v);
    }
  };

  return (
    <div>
      <div role={multi ? 'group' : 'radiogroup'} className={`grid grid-cols-1 gap-2 ${SM_COLS[q.columns] || ''}`}>
        {q.options.map((o) => {
          const on = selected(o.value);
          return (
            <button
              key={o.value}
              type="button"
              role={multi ? 'checkbox' : 'radio'}
              aria-checked={on}
              onClick={() => toggle(o.value)}
              className={`flex items-center gap-3 w-full text-left rounded-lg border px-4 py-3 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                on ? 'border-primary-500 bg-primary-50 text-primary-900' : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:bg-gray-50'
              }`}
            >
              <span
                className={`flex-shrink-0 h-4 w-4 border flex items-center justify-center transition-colors ${multi ? 'rounded' : 'rounded-full'} ${
                  on ? 'border-primary-600 bg-primary-600' : 'border-gray-300 bg-white'
                }`}
              >
                {on && (multi ? <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} /> : <span className="h-1.5 w-1.5 rounded-full bg-white" />)}
              </span>
              <span className="leading-snug">{o.label}</span>
            </button>
          );
        })}
      </div>
      <AnimatePresence initial={false}>
        {otherChosen && (
          <motion.div {...REVEAL}>
            <input
              type="text"
              value={answers[`${q.id}_other`] || ''}
              onChange={(e) => setAnswer(`${q.id}_other`, e.target.value)}
              placeholder="Please specify"
              aria-label="Please specify"
              className="mt-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Ordinal scale: one button per label, stored as the 1-based position.
const ScaleInput = ({ id, scale, value, setAnswer, label }) => (
  <div role="radiogroup" aria-label={label} className={`grid grid-cols-1 gap-2 ${SM_COLS[scale.length] || ''}`}>
    {scale.map((text, i) => {
      const v = i + 1;
      const on = Number(value) === v;
      return (
        <button
          key={text}
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => setAnswer(id, on ? null : v)}
          className={`flex sm:flex-col items-center sm:justify-start gap-3 sm:gap-1.5 rounded-lg border px-4 sm:px-2 py-2.5 sm:py-3 text-sm sm:text-xs text-left sm:text-center leading-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            on ? 'border-primary-500 bg-primary-50 text-primary-900 font-medium' : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300 hover:bg-gray-50'
          }`}
        >
          <span
            className={`flex-shrink-0 h-6 w-6 rounded-full border text-[11px] font-semibold flex items-center justify-center transition-colors ${
              on ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 text-gray-400'
            }`}
          >
            {v}
          </span>
          <span>{text}</span>
        </button>
      );
    })}
  </div>
);

// ---------------------------------------------------------------------------
// Question cards
// ---------------------------------------------------------------------------

const NumberBadge = ({ n }) => (
  <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-primary-50 text-primary-700 text-xs font-semibold">
    Q{n}
  </span>
);

// Label shown in place of a question number (follow-ups, comment boxes).
const TagBadge = ({ children }) => (
  <span className="flex-shrink-0 inline-flex items-center h-6 px-2 rounded-md bg-gray-100 text-gray-600 text-[11px] font-semibold uppercase tracking-wide">
    {children}
  </span>
);

const Question = ({ q, answers, setAnswer }) => {
  if (q.type === 'statements') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-5 pt-5 pb-1">
          <p className="text-[15px] font-medium text-gray-900 leading-snug">{q.text}</p>
        </div>
        <div className="divide-y divide-gray-100">
          {q.items.map((it) => (
            <div key={it.id} className="px-5 py-4">
              <div className="flex items-start gap-3 mb-3">
                <NumberBadge n={questionNumber(it.id)} />
                <p className="text-sm text-gray-800 leading-relaxed">{it.text}</p>
              </div>
              <ScaleInput id={it.id} scale={q.scale} value={answers[it.id]} setAnswer={setAnswer} label={it.text} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-xl border shadow-sm p-5 ${
        q.followUp ? 'border-primary-200 border-l-4 border-l-primary-400' : q.comment ? 'border-dashed border-gray-300' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start gap-3 mb-4">
        {q.followUp ? <TagBadge>Follow-up</TagBadge> : q.comment ? <TagBadge>Comments</TagBadge> : <NumberBadge n={questionNumber(q.id)} />}
        <div>
          <p className="text-[15px] font-medium text-gray-900 leading-snug">{q.text}</p>
          {q.type === 'multi' && <p className="text-xs text-gray-500 mt-1">Select all that apply.</p>}
        </div>
      </div>
      {(q.type === 'single' || q.type === 'multi') && <ChoiceList q={q} answers={answers} setAnswer={setAnswer} />}
      {q.type === 'scale' && <ScaleInput id={q.id} scale={q.scale} value={answers[q.id]} setAnswer={setAnswer} label={q.text} />}
      {q.type === 'text' && (
        <>
          <textarea
            value={answers[q.id] || ''}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            rows={q.comment ? 3 : 2}
            maxLength={q.maxLength}
            placeholder={q.placeholder || 'Type your answer…'}
            aria-label={q.text}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-y"
          />
          {q.maxLength && (
            <p className="mt-1 text-right text-[11px] text-gray-400">
              {(answers[q.id] || '').length} / {q.maxLength}
            </p>
          )}
        </>
      )}
    </div>
  );
};

// Section background text: full card on the section's first page, a
// collapsible reminder on the pages after it.
const ContextCard = ({ context, collapsed }) => {
  if (collapsed) {
    return (
      <details className="group bg-primary-50/60 border border-primary-100 rounded-xl px-4 py-3 text-sm">
        <summary className="cursor-pointer select-none font-medium text-primary-800 flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden">
          <InformationCircleIcon className="h-4 w-4" />
          {context.reminder}
          <ChevronRightIcon className="h-3.5 w-3.5 ml-auto transition-transform group-open:rotate-90" />
        </summary>
        <p className="mt-2 text-gray-700 leading-relaxed">{context.text}</p>
      </details>
    );
  }
  return (
    <div className="bg-primary-50/60 border border-primary-100 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-primary-900 flex items-center gap-2 mb-2">
        <InformationCircleIcon className="h-5 w-5 text-primary-600" />
        {context.title}
      </h3>
      <p className="text-sm text-gray-700 leading-relaxed">{context.text}</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Survey = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(INTRO_STEP);
  const [started, setStarted] = useState(false);       // a response row exists / user began
  // 'completed' shows the submitted copy; anything else shows the survey pages.
  const [status, setStatus] = useState(null);
  // How often it was submitted (> 0 means the pages are editing a submitted response).
  const [submission, setSubmission] = useState({ count: 0, firstAt: null, lastAt: null });
  const [edited, setEdited] = useState(false);          // unsubmitted changes since the last submit
  const [saveState, setSaveState] = useState('idle');   // idle | saving | saved | error
  const [submitting, setSubmitting] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const mainRef = useRef(null);

  // Latest values for the (debounced) saver, which runs outside render.
  const answersRef = useRef(answers);
  const stepRef = useRef(step);
  const submissionRef = useRef(submission);
  answersRef.current = answers;
  stepRef.current = step;
  submissionRef.current = submission;

  // Saves run strictly one after another so an older payload can never land
  // after a newer one. Nothing is saved until the user actually does something.
  const saveChain = useRef(Promise.resolve());
  const saveTimer = useRef(null);
  const dirty = useRef(false);
  const lastPage = useRef(1); // where "Continue survey" resumes from the intro

  // Sync status + submission info from a response row returned by the API.
  const applyServer = useCallback((r) => {
    setStatus(r.status);
    setSubmission({ count: r.submission_count || 0, firstAt: r.completed_at, lastAt: r.last_submitted_at });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await surveyAPI.getMyResponse(SURVEY_ID);
      const r = res.data?.response;
      if (r) {
        setAnswers(r.answers || {});
        setStarted(true);
        applyServer(r);
        if (r.status !== 'completed') {
          // Resume where they left off (a first attempt, or unsubmitted edits).
          const s = Math.min(Math.max(Number(r.last_step) || INTRO_STEP, INTRO_STEP), REVIEW_STEP);
          setStep(s);
          if (s > INTRO_STEP) lastPage.current = s;
          setEdited(r.submission_count > 0 || !!r.completed_at);
        }
      }
      setLoading(false);
    } catch (e) {
      // Don't let the user start over on top of answers we failed to load.
      setLoadError(true);
      setLoading(false);
    }
  }, [applyServer]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback((completed = false) => {
    const payload = {
      surveyId: SURVEY_ID,
      answers: pruneAnswers(answersRef.current),
      lastStep: stepRef.current,
      completed,
      editing: wasSubmitted(submissionRef.current),
    };
    setSaveState('saving');
    const p = saveChain.current.then(() => surveyAPI.saveResponse(payload));
    saveChain.current = p.catch(() => {});
    p.then(() => setSaveState('saved')).catch((err) => {
      const r = err.response?.status === 409 && err.response.data?.response;
      if (r) {
        // Submitted elsewhere (e.g. another tab) — show what was submitted.
        setAnswers(r.answers || {});
        applyServer(r);
      }
      setSaveState('error');
    });
    return p;
  }, [applyServer]);

  const flushPendingSave = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      save();
    }
  }, [save]);

  // Autosave shortly after answers or position change.
  useEffect(() => {
    if (!dirty.current || status === 'completed') return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      save();
    }, 800);
    return undefined;
  }, [answers, step, status, save]);

  // Don't drop the last edits if the user leaves the page mid-debounce.
  useEffect(() => () => flushPendingSave(), [flushPendingSave]);

  const setAnswer = useCallback((key, value) => {
    dirty.current = true;
    setStarted(true);
    setEdited(true);
    setAnswers((prev) => {
      const next = { ...prev };
      if (value === null || value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  const goTo = (s) => {
    const next = Math.min(Math.max(s, INTRO_STEP), REVIEW_STEP);
    // Save the position on a first attempt; when editing a submitted response,
    // only an actual answer change should reopen it.
    if (!wasSubmitted(submissionRef.current)) dirty.current = true;
    setStarted(true);
    setStep(next);
    if (next > INTRO_STEP) lastPage.current = next;
    mainRef.current?.scrollTo({ top: 0 });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = null;
    const resubmit = wasSubmitted(submissionRef.current);
    try {
      const res = await save(true);
      const r = res.data.response;
      setAnswers(r.answers || {});
      applyServer(r);
      dirty.current = false;
      setEdited(false);
      if (resubmit) toast.success('Your changes have been submitted.');
    } catch (e) {
      // saveState shows the error; stay on the review step so they can retry.
    } finally {
      setSubmitting(false);
    }
  };

  // Open the submitted response for editing at the given step. Nothing is
  // saved until an answer actually changes.
  const startEditing = (s = 1) => {
    dirty.current = false;
    setEdited(false);
    setSaveState('idle');
    setStatus('in_progress');
    setStep(s);
    lastPage.current = s;
  };

  // Leave edit mode, restoring the last submitted answers on the server too.
  const handleDiscard = async () => {
    clearTimeout(saveTimer.current);
    saveTimer.current = null;
    setDiscarding(true);
    try {
      const p = saveChain.current.then(() => surveyAPI.discardEdits(SURVEY_ID));
      saveChain.current = p.catch(() => {});
      const r = (await p).data.response;
      setAnswers(r.answers || {});
      applyServer(r);
      dirty.current = false;
      setEdited(false);
      setSaveState('idle');
    } catch (e) {
      toast.error('Couldn’t discard your changes. Please try again.');
    } finally {
      setDiscarding(false);
    }
  };

  // ------------------------------------------------------------------ render

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-gray-50" style={{ height: 'calc(100vh - 4rem)' }}>
        <span className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center bg-gray-50 px-4" style={{ height: 'calc(100vh - 4rem)' }}>
        <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500 mx-auto mb-3" />
          <h2 className="text-base font-semibold text-gray-900">We couldn’t load the survey</h2>
          <p className="text-sm text-gray-600 mt-1">Please check your connection and try again.</p>
          <button onClick={load} className="mt-4 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (status === 'completed') {
    const updated = submission.count > 1;
    return (
      // Keyed so switching views mounts a fresh scroll container (starts at the top).
      <div key="submitted" className="bg-gray-50 overflow-y-auto" style={{ height: 'calc(100vh - 4rem)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <CheckCircleIcon className="h-10 w-10 text-emerald-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900">Thank you! Your response has been submitted.</h1>
              <p className="text-sm text-gray-600 mt-1">
                Submitted {formatDate(submission.firstAt)}
                {updated && ` · last updated ${formatDate(submission.lastAt)}`}.
                {' '}Below is a copy of your full response — you can edit it at any time.
              </p>
            </div>
            <button
              onClick={() => startEditing(1)}
              className="inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-all shadow-sm whitespace-nowrap"
            >
              <PencilSquareIcon className="h-4 w-4 mr-1.5" />
              Edit responses
            </button>
          </motion.div>

          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Your responses</h2>
            <ResponseSummary answers={answers} onEditSection={(i) => startEditing(firstStepOf(i))} />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pb-4">
            <Link to="/chat" className="px-5 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium text-center hover:bg-gray-50 transition-colors">
              Go to Chat
            </Link>
            <Link to="/experimental" className="px-5 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium text-center hover:bg-gray-50 transition-colors">
              Open Explorer
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const editing = wasSubmitted(submission);
  const current = step >= 1 && step <= PAGES.length ? PAGES[step - 1] : null;
  const visibleQuestions = current ? current.page.questions.filter((q) => isVisible(q, answers)) : [];
  const progress = Math.round((step / REVIEW_STEP) * 100);
  const [answeredAll, totalAll] = countAnswered(PAGES.map((p) => p.page), answers);

  const saveLabel = {
    saving: 'Saving…',
    saved: 'All changes saved',
    error: 'Couldn’t save — we’ll retry on your next change',
    idle: '',
  }[saveState];

  return (
    <div key="survey" className="flex bg-gray-50" style={{ height: 'calc(100vh - 4rem)' }}>
      {/* Sidebar: section outline */}
      <aside className="hidden lg:flex w-72 border-r border-gray-200 bg-white flex-col flex-shrink-0">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-800 flex items-center">
            <ClipboardDocumentListIcon className="h-4 w-4 mr-2 text-primary-500" />
            {SURVEY_TITLE}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">{answeredAll} of {totalAll} questions answered</p>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          <SidebarItem
            label="Welcome"
            sub="About this survey"
            active={step === INTRO_STEP}
            done={started}
            index={<InformationCircleIcon className="h-4 w-4" />}
            onClick={() => goTo(INTRO_STEP)}
          />
          {SECTIONS.map((s, i) => {
            const [a, t] = countAnswered(s.pages, answers);
            const active = current?.sectionIndex === i;
            return (
              <div key={s.id}>
                <SidebarItem
                  label={s.shortTitle}
                  sub={`${rangeLabel(questionNumbers(s.pages))} · ${a} of ${t} answered`}
                  active={active}
                  done={t > 0 && a === t}
                  index={i + 1}
                  onClick={() => goTo(firstStepOf(i))}
                />
                {/* The current section's pages, so the counts above map onto what's on screen. */}
                {active && <SectionPages sectionIndex={i} step={step} answers={answers} onGo={goTo} />}
              </div>
            );
          })}
          <SidebarItem
            label="Review & submit"
            sub="Check your answers"
            active={step === REVIEW_STEP}
            index={<CheckIcon className="h-4 w-4" />}
            onClick={() => goTo(REVIEW_STEP)}
          />
        </nav>
        <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-400">
          All questions are optional. Progress saves automatically.
        </div>
      </aside>

      {/* Main */}
      <main ref={mainRef} className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          {editing && (
            <div className="mb-5 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <PencilSquareIcon className="hidden sm:block h-5 w-5 text-primary-600 flex-shrink-0" />
              <p className="text-sm text-primary-900 flex-1">
                You’re editing the response you submitted {formatDate(submission.lastAt)}.
                {' '}Changes are saved as a draft until you submit them from the review page — your submitted answers stay on record.
              </p>
              <button
                onClick={handleDiscard}
                disabled={discarding}
                className="self-start sm:self-auto px-3 py-1.5 rounded-md border border-primary-200 bg-white text-sm font-medium text-primary-700 hover:bg-primary-100 transition-colors whitespace-nowrap disabled:opacity-60"
              >
                {discarding ? 'Discarding…' : edited ? 'Discard changes' : 'Back to my responses'}
              </button>
            </div>
          )}

          {/* Header + progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-xs font-medium text-gray-500">
                {current
                  ? [
                      `Section ${current.sectionIndex + 1} of ${SECTIONS.length}`,
                      `Page ${current.pageIndex + 1} of ${current.section.pages.length}`,
                      rangeLabel(questionNumbers([current.page])),
                    ].filter(Boolean).join(' · ')
                  : step === REVIEW_STEP ? 'Review & submit' : SURVEY_TITLE}
              </p>
              {started && saveLabel && (
                <p className={`text-xs ${saveState === 'error' ? 'text-rose-600' : 'text-gray-400'}`}>{saveLabel}</p>
              )}
            </div>
            <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-primary-600 to-primary-400" initial={false} animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.2 }}
            >
              {step === INTRO_STEP && <IntroStep started={started} onStart={() => goTo(lastPage.current)} />}

              {current && (
                <div className="space-y-4">
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">{current.section.title}</h1>
                    {current.pageIndex === 0 && <p className="text-sm text-gray-500 mt-1">{current.section.blurb}</p>}
                  </div>

                  {current.section.context && <ContextCard context={current.section.context} collapsed={current.pageIndex > 0} />}

                  {current.page.questions.length === 0 && (
                    <p className="text-sm text-gray-500">Please read the description above, then continue to share your impressions.</p>
                  )}

                  <AnimatePresence initial={false}>
                    {visibleQuestions.map((q) => (
                      <motion.div key={q.id} {...REVEAL}>
                        <Question q={q} answers={answers} setAnswer={setAnswer} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}

              {step === REVIEW_STEP && (
                <ReviewStep answers={answers} answeredAll={answeredAll} totalAll={totalAll} onGo={goTo} editing={editing} />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {step !== INTRO_STEP && (
            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                onClick={() => goTo(step - 1)}
                className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-gray-700 bg-white rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Back
              </button>
              {step < REVIEW_STEP ? (
                <button
                  onClick={() => goTo(step + 1)}
                  className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-all shadow-sm"
                >
                  {step === PAGES.length ? 'Review answers' : 'Next'}
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Submitting…
                    </>
                  ) : (
                    editing ? 'Submit changes' : 'Submit survey'
                  )}
                </button>
              )}
            </div>
          )}
          {step === REVIEW_STEP && saveState === 'error' && !submitting && (
            <p className="mt-3 text-sm text-rose-600 text-right">Your survey couldn’t be submitted. Please try again.</p>
          )}
        </div>
      </main>
    </div>
  );
};

const SidebarItem = ({ label, sub, active, done, index, onClick }) => (
  <button
    onClick={onClick}
    aria-current={active ? 'step' : undefined}
    className={`w-full flex items-center gap-3 text-left rounded-lg border px-3 py-2 transition-colors ${
      active ? 'bg-primary-50 border-primary-300' : 'bg-white border-transparent hover:bg-gray-50'
    }`}
  >
    <span
      className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
        done ? 'bg-emerald-100 text-emerald-700' : active ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {done ? <CheckIcon className="h-4 w-4" strokeWidth={2.5} /> : index}
    </span>
    <span className="min-w-0">
      <span className={`block text-sm font-medium truncate ${active ? 'text-primary-800' : 'text-gray-800'}`}>{label}</span>
      <span className="block text-xs text-gray-500 truncate">{sub}</span>
    </span>
  </button>
);

// Pages of a section as sub-steps: question range + how many are answered.
const SectionPages = ({ sectionIndex, step, answers, onGo }) => {
  const first = firstStepOf(sectionIndex);
  return (
    <div className="ml-[1.625rem] pl-3 border-l border-gray-200 my-1 space-y-0.5">
      {SECTIONS[sectionIndex].pages.map((p, j) => {
        const pageStep = first + j;
        const [a, t] = countAnswered([p], answers);
        const active = pageStep === step;
        return (
          <button
            key={j}
            onClick={() => onGo(pageStep)}
            aria-current={active ? 'step' : undefined}
            className={`w-full flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors ${
              active ? 'bg-primary-50 text-primary-800 font-medium' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span>Page {j + 1} · {t > 0 ? rangeLabel(questionNumbers([p])) : 'Description'}</span>
            {t > 0 && (a === t
              ? <CheckIcon className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} />
              : <span className="text-gray-400">{a} of {t}</span>)}
          </button>
        );
      })}
    </div>
  );
};

const IntroStep = ({ started, onStart }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-white">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">MGH × MIT research study</p>
      <h1 className="text-2xl font-bold text-gray-900 mt-1">{SURVEY_TITLE}</h1>
    </div>
    <div className="p-6 space-y-5">
      <p className="text-sm text-gray-700 leading-relaxed">{INTRO_TEXT}</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700">
          <ClockIcon className="h-4 w-4 text-primary-500 flex-shrink-0" /> 10-15 minutes
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700">
          <ClipboardDocumentListIcon className="h-4 w-4 text-primary-500 flex-shrink-0" /> {SECTIONS.length} short sections
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700">
          <CheckCircleIcon className="h-4 w-4 text-primary-500 flex-shrink-0" /> All questions optional
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">What we’ll ask about</p>
        <ol className="space-y-2">
          {SECTIONS.map((s, i) => (
            <li key={s.id} className="flex items-start gap-3">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold flex items-center justify-center">{i + 1}</span>
              <span className="text-sm">
                <span className="font-medium text-gray-900">{s.shortTitle}</span>
                <span className="text-gray-500"> — {s.blurb}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>

      <p className="text-xs text-gray-500">Your progress is saved as you go, so you can leave and come back to finish later.</p>

      <button
        onClick={onStart}
        className="w-full py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg hover:from-primary-700 hover:to-primary-600 transition-all shadow-sm text-sm font-medium flex items-center justify-center"
      >
        {started ? 'Continue survey' : 'Start survey'}
        <ChevronRightIcon className="h-4 w-4 ml-1" />
      </button>
    </div>
  </div>
);

const ReviewStep = ({ answers, answeredAll, totalAll, onGo, editing }) => {
  const skipped = totalAll - answeredAll;
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Review & submit</h1>
        <p className="text-sm text-gray-500 mt-1">You can go back to any section before submitting.</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
        {SECTIONS.map((s, i) => {
          const [a, t] = countAnswered(s.pages, answers);
          const complete = a === t;
          const missing = unansweredNumbers(s.pages, answers);
          return (
            <div key={s.id} className="flex items-start gap-3 px-5 py-4">
              <span className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${complete ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {complete ? <CheckIcon className="h-4 w-4" strokeWidth={2.5} /> : i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{s.title}</p>
                <p className={`text-xs ${complete ? 'text-emerald-600' : 'text-gray-500'}`}>
                  {rangeLabel(questionNumbers(s.pages))} · {complete ? 'All questions answered' : `${a} of ${t} answered`}
                </p>
                {missing.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="text-xs text-gray-500">Unanswered:</span>
                    {missing.map((n) => (
                      <button
                        key={n}
                        onClick={() => onGo(STEP_OF_QUESTION[n])}
                        title={`Go to question ${n}`}
                        className="px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800 text-[11px] font-semibold hover:bg-amber-100 transition-colors"
                      >
                        Q{n}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => onGo(firstStepOf(i))} className="inline-flex items-center text-xs font-medium text-primary-600 hover:text-primary-700">
                <PencilSquareIcon className="h-4 w-4 mr-1" />
                {complete ? 'Edit' : 'Review'}
              </button>
            </div>
          );
        })}
      </div>

      {skipped > 0 ? (
        <div className="rounded-lg p-4 border bg-yellow-50 border-yellow-200 text-sm text-yellow-800">
          You left {skipped} question{skipped === 1 ? '' : 's'} unanswered. That’s okay — every question is optional — but complete answers are most helpful to our research.
        </div>
      ) : (
        <div className="rounded-lg p-4 border bg-emerald-50 border-emerald-200 text-sm text-emerald-800">
          You’ve answered every question. Thank you!
        </div>
      )}

      <p className="text-xs text-gray-500">
        {editing
          ? 'Submitting saves your changes as a new version of your response; your earlier submission stays on record.'
          : 'After submitting, you’ll see a copy of your full response and can still edit it.'}
      </p>
    </div>
  );
};

export default Survey;
